<template>
  <div class="step2-container">
    <!-- 환자 정보 입력 -->
    <CustomerInfoForm
      ref="customerInfoForm"
      data-tour="s2-customer"
      v-model="customerInfo"
      :windowInnerWidthGreaterThan1000="true"
      :showDoctorInput="false"
      :showDoctorNameInput="true"
      :isCustomDoctor="false"
      :doctorList="[]"
      :sendChannel="sendChannel"
      :language="language"
      :countryCodeList="countryCodeList"
      :supportMsgChannel="supportMsgChannel"
      :channelConfig="channelConfig"
      @update:customerInfo="handleCustomerInfoUpdate"
      @update:sendChannel="handleSendChannelUpdate"
      @update:language="handleLanguageUpdate"
      @update:canLeavePage="handleCanLeavePageUpdate"
      @focus-on-next-step-button="handleFocusOnNextStepButton"
      @update:isCustomDoctor="handleIsCustomDoctorUpdate"
    />

    <!-- 작업자 정보 입력 -->
    <WorkerInfoForm
      style="margin-top: 15px;"
      ref="workerInfoForm"
      data-tour="s2-worker"
      v-model="workerInfo"
      :windowInnerWidthGreaterThan1000="true"
    />
  </div>
</template>

<script>
import CustomerInfoForm from './CustomerInfoForm.vue';
import WorkerInfoForm from './WorkerInfoForm.vue';
import { commonEnum, buildPrimaryCountryCodeList } from './enums/common';
import { localStorageEnums } from "@/enums/localStorage.enums";

/**
 * 이 admin이 지원하는 인증서 전송 수단. 지원 여부를 정하는 유일한 출처다.
 *
 * - 목록의 첫 항목이 기본 전송 수단이다.
 * - 항목이 2개 이상이면 CustomerInfoForm이 전송 방식 선택 UI를 노출하고, 1개면 숨긴다.
 * - 전송 수단은 locale·국가번호·언어 등 다른 값에서 유추하지 않는다. 추가·제거는 이 배열만 고친다.
 *
 * 현재: 위챗만 지원. 카카오톡은 중국 환경에서 의미가 없고, 이메일은 미지원이다(2026-08-13).
 */
const SUPPORT_MSG_CHANNEL = ['WECHAT'];

export default {
  name: 'Step2',
  components: {
    CustomerInfoForm,
    WorkerInfoForm
  },
  props: {
    // 부모(index.vue)의 certificateInfo를 하향 전달받는다(부모가 SSOT).
    // Step3에서 환자 자가발급 역연결이 감지되면 부모 certificateInfo가 채워지는데,
    // Step2는 keep-alive로 캐시된 별도 인스턴스라 그 값이 자동으로 도달하지 못했다(이름 소실 버그).
    // 이 prop + watch로 부모가 채운 환자신원을 Step2 화면(customerInfo)에 반영한다(편집 가능, 잠그지 않음).
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      customerInfo: {
        email: '',
        mobile: '',
        name: '',
        gender: '',
        countryCode: '+86',
        birth: null,
        surgeryDate: (() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        })(),
        agreements: [],
        doctor: '',
        id: '',
        teethNos: [],
        ongoingSurgeries: [],
        chartNumber: '', // 차트번호 추가
        doctorName: '' // 의사명(옵셔널 자유입력)
      },
      workerInfo: {
        name: '',
        workDate: (() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        })(),
        prostheticDeliveryDate: (() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        })() // 보철 납품일 기본값을 오늘 날짜로 설정
      },
      // 전송 수단의 기본값은 지원 목록의 첫 항목이다(SUPPORT_MSG_CHANNEL 주석 참조).
      // language는 locale이 결정하며 전송 수단과 무관하다.
      sendChannel: commonEnum.sendChannel[SUPPORT_MSG_CHANNEL[0]],
      language: commonEnum.language.ZH,
      supportMsgChannel: [...SUPPORT_MSG_CHANNEL],
      // 채널별 표시 설정(아이콘·문구) 조회표. supportMsgChannel에 올라온 채널만 실제로 사용되며,
      // 미사용 항목을 남겨둬도 렌더에 영향이 없다.
      channelConfig: {
        WECHAT: {
          icon: 'fa-brands fa-weixin',
          style: { color: '#7BB32E' },
          translationKey: 'dent_issue_certificate_card_container_language_sendtype_wechat'
        },
        EMAIL: {
          icon: 'fa-solid fa-envelope',
          style: { color: '#0066CC' },
          translationKey: 'dent_issue_certificate_card_container_language_sendtype_email'
        }
      }
    };
  },
  computed: {
    // 연락처 국가번호 선택지 — commonEnum 단일 원천에서 조립한다.
    // created()에서 배열을 만들면 그 시점 언어로 박제되므로 computed로 둬 locale 변경에 반응시킨다.
    // 종전에는 여기서 +82/+86/+1 3개를 자체 하드코딩하는 바람에 위챗 사용권인
    // 홍콩·마카오·대만·싱가포르가 선택지에서 빠져 있었다(2026-08-13 수정).
    countryCodeList() {
      return buildPrimaryCountryCodeList(this.$t.bind(this));
    }
  },
  created() {
    // locale에 따른 기본값 설정
    this.initDefaultsByLocale();

    // 최초 마운트 시 부모가 이미 채워둔 환자신원(자가발급 pre-fill 등)이 있으면 반영한다.
    // (보통 최초 진입 시 initialData는 비어 있어 no-op이고, 실제 자가발급 값은 이후 watch가 반영한다.)
    this.applyInitialData(this.initialData);
  },
  watch: {
    // 부모 certificateInfo가 바뀔 때(Step3 자가발급 감지로 이름/전화/성별/생년/수술일이 채워지면)
    // Step2가 keep-alive로 비활성(deactivated) 상태여도 watch는 계속 동작하므로,
    // 그 값을 로컬 customerInfo에 병합해 Step2 화면·저장(getFormData) 값에 반영한다.
    // 사용자가 이후 Step2에서 직접 수정하면 그 수정값이 getFormData로 저장되어 권위를 갖는다.
    initialData: {
      handler(newVal) {
        this.applyInitialData(newVal);
      },
      deep: true
    }
  },
  methods: {
    // 부모가 내려준 환자신원(certificateInfo 형태: name/displayMobile/mobile/gender/birthDate/surgeryDate 등)을
    // Step2 내부 customerInfo(name/mobile/gender/birth/surgeryDate 등) 필드로 매핑해 병합한다.
    // [HARD] 값이 있는 필드만 덮어써(있으면 우선), 응답에 없는 필드는 기존 기본값/사용자 입력을 보존한다.
    // Vue3/Options: 로컬 data 복사 시 반응성 함정 방지를 위해 새 객체로 재할당한다(직접 참조 변형 아님).
    applyInitialData(data) {
      if (!data || typeof data !== 'object') return;

      const patch = {};
      if (data.name) patch.name = data.name;
      // customerInfo.mobile은 화면 표시용(포맷 포함) 값이므로 displayMobile을 우선 사용한다.
      const mobile = data.displayMobile || data.mobile;
      if (mobile) patch.mobile = mobile;
      if (data.gender) patch.gender = data.gender;
      if (data.birthDate) patch.birth = data.birthDate;
      if (data.surgeryDate) patch.surgeryDate = data.surgeryDate;
      if (data.countryCode) patch.countryCode = data.countryCode;
      if (data.email) patch.email = data.email;
      if (data.chartNumber) patch.chartNumber = data.chartNumber;
      if (data.doctorName) patch.doctorName = data.doctorName;

      if (Object.keys(patch).length === 0) return;

      this.customerInfo = { ...this.customerInfo, ...patch };
    },

    // locale에 따른 기본값 초기화 — 국가번호와 안내 언어만 결정한다.
    // 전송 수단은 여기서 다루지 않는다: locale로 전송 수단을 유추하면(ko→카카오톡, en→이메일)
    // 지원하지도 않는 채널이 기본값으로 박히고, 그 규칙이 코드 어디에도 드러나지 않는 암묵지가 된다.
    // 지원 전송 수단은 SUPPORT_MSG_CHANNEL 하나로만 정한다.
    initDefaultsByLocale() {
      try {
        const companyInfo = JSON.parse(localStorage.getItem(localStorageEnums.CompanyInfo));
        // @MX:NOTE: china admin은 zh fallback. 한국 admin과 분기되는 핵심 라인.
        const locale = companyInfo?.locale || 'zh';

        // locale별 기본값 매핑
        const localeDefaults = {
          ko: {
            countryCode: '+82',
            language: commonEnum.language.KO
          },
          zh: {
            countryCode: '+86',
            language: commonEnum.language.ZH
          },
          en: {
            countryCode: '+1',
            language: commonEnum.language.EN
          }
        };

        // 해당 locale의 기본값 적용 (없으면 zh 기본값 사용 — china admin)
        const defaults = localeDefaults[locale] || localeDefaults.zh;

        this.customerInfo.countryCode = defaults.countryCode;
        this.language = defaults.language;
      } catch (error) {
        console.error('locale 기본값 설정 오류:', error);
      }
    },

    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    },
    // CustomerInfoForm의 이벤트 핸들러들
    handleCustomerInfoUpdate(customerInfo) {
      this.customerInfo = customerInfo;
    },

    handleSendChannelUpdate(sendChannel) {
      this.sendChannel = sendChannel;
    },

    handleLanguageUpdate(language) {
      this.language = language;
    },

    handleCanLeavePageUpdate(canLeavePage) {
      // 필요시 처리
    },

    handleFocusOnNextStepButton() {
      // 필요시 처리
    },

    handleIsCustomDoctorUpdate(isCustomDoctor) {
      // 필요시 처리
    },

    // 부모 컴포넌트에서 호출할 검증 메서드 (기존 Step2 호환성 유지)
    async validateForm() {
      let customerValid = false;
      let workerValid = false;

      if (this.$refs.customerInfoForm) {
        customerValid = await this.$refs.customerInfoForm.validateForm();
      }

      if (this.$refs.workerInfoForm) {
        workerValid = await this.$refs.workerInfoForm.validateForm();
      }

      return customerValid && workerValid;
    },

    // 부모 컴포넌트에서 호출할 데이터 가져오기 메서드 (기존 Step2 호환성 유지)
    getFormData() {
      let customerData = {};
      let workerData = {};

      if (this.$refs.customerInfoForm) {
        const data = this.$refs.customerInfoForm.getFormData();
        // API 요구사항에 맞게 필드명 매핑
        customerData = {
          name: data.name,
          mobile: data.mobile?.replace(/\D/g, '') || '', // 숫자만 추출
          displayMobile: data.mobile || '',
          email: data.email || '',
          countryCode: data.countryCode || '+86',
          gender: data.gender || '',
          birthDate: data.birth || '',
          surgeryDate: data.surgeryDate || '',
          chartNumber: data.chartNumber || '', // 차트번호 추가
          doctorName: data.doctorName || '' // 의사명(옵셔널)
        };
      } else {
        customerData = {
          name: this.customerInfo.name,
          mobile: this.customerInfo.mobile?.replace(/\D/g, '') || '',
          displayMobile: this.customerInfo.mobile || '',
          email: this.customerInfo.email || '',
          countryCode: this.customerInfo.countryCode || '+86',
          gender: this.customerInfo.gender || '',
          birthDate: this.customerInfo.birth || '',
          surgeryDate: this.customerInfo.surgeryDate || '',
          chartNumber: this.customerInfo.chartNumber || '', // 차트번호 추가
          doctorName: this.customerInfo.doctorName || '' // 의사명(옵셔널)
        };
      }

      if (this.$refs.workerInfoForm) {
        workerData = this.$refs.workerInfoForm.getFormData();
      } else {
        workerData = {
          name: this.workerInfo.name || '',
          workDate: this.workerInfo.workDate || '',
          prostheticDeliveryDate: this.workerInfo.prostheticDeliveryDate || '' // 보철 납품일 추가
        };
      }

      return {
        ...customerData,
        worker: workerData,
        // 언어 및 전송방식 정보 추가
        language: this.language,
        sendChannel: this.sendChannel
      };
    }
  }
};
</script>
